// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import type { PubmedPaperDetails } from '../../../../../services/pubmedService';
import type { AbstractTriageBrief } from '../../../lib/models';
import { formatJournalNameAPA } from '../utils/formatJournalNameAPA';
import { processAbstractText } from '../utils/processAbstractText';
import { getValidAuthors } from '../utils/getValidAuthors';
import { EnhancedStudySummary } from './EnhancedStudySummary';
import './EnhancedStudySummary.css';

export interface DetailsViewProps {
  paper: PubmedPaperDetails | null;
  onBack: () => void;
  isLoading?: boolean;
  error?: string | null;
  onSaveToLibrary?: () => void;
  onAttachToScenario?: () => void;
  aiBrief?: AbstractTriageBrief;
  onTriageRequest?: (pmid: string, signal: AbortSignal) => Promise<void>;
}

export const DetailsView: React.FC<DetailsViewProps> = ({ 
  paper, 
  onBack, 
  isLoading = false,
  error = null,
  onSaveToLibrary, 
  onAttachToScenario,
  aiBrief,
  onTriageRequest
}) => {
  const [isTriaging, setIsTriaging] = useState(false);
  const [triageError, setTriageError] = useState<string | null>(null);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  // Reset local state when paper changes
  useEffect(() => {
    setTriageError(null);
    abortControllerRef.current?.abort();
  }, [paper?.pmid]);

  // Cleanup on unmount
  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const handleTriage = async () => {
    if (!paper || !onTriageRequest) return;

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    setIsTriaging(true);
    setTriageError(null);
    
    try {
      await onTriageRequest(paper.pmid, signal);
      if (signal.aborted) return;
      setIsTriaging(false);
    } catch (err: any) {
      if (signal.aborted) return;
      console.error('Single triage failed:', err);
      setIsTriaging(false);
      setTriageError(err.message || 'AI triage failed');
    }
  };

  if (isLoading) {
    return (
      <div className="evidence-drawer__details-view">
        <button className="evidence-drawer__details-back" onClick={onBack}>
          ← Back
        </button>
        <div className="evidence-drawer__loading">Loading details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="evidence-drawer__details-view">
        <button className="evidence-drawer__details-back" onClick={onBack}>
          ← Back
        </button>
        <div className="evidence-drawer__error">{error}</div>
      </div>
    );
  }

  if (!paper) return null;

  const validAuthors = getValidAuthors(paper.authors);
  const canTriage = !!paper.abstract && !aiBrief && !!onTriageRequest;
  const hasRelevantParams = !!aiBrief && (
    (aiBrief.potentialInputSources?.some(source => source !== 'none') ?? false) ||
    (aiBrief.retrievable_inputs?.length ?? 0) > 0
  );

  return (
    <div className="evidence-drawer__details-view">
      <button className="evidence-drawer__details-back" onClick={onBack}>
        ← Back
      </button>
      <div className="evidence-drawer__details-content">
        <h3 className="evidence-drawer__details-title">{paper.title}</h3>
        
        {(validAuthors.length > 0) || paper.year || paper.journal ? (
          <div className="evidence-drawer__details-meta-section">
            {typeof paper.year === 'number' && paper.year > 0 && (
              <span className="evidence-drawer__meta-item">{paper.year}</span>
            )}
            {paper.journal && <span className="evidence-drawer__meta-item">{formatJournalNameAPA(paper.journal)}</span>}
            {validAuthors.length > 0 && (
              <span className="evidence-drawer__meta-item">
                {validAuthors.length === 1 ? validAuthors[0] : `${validAuthors[0]} et al.`}
              </span>
            )}
          </div>
        ) : null}

        {/* AI Triage Section */}
        {aiBrief && hasRelevantParams && (
          <EnhancedStudySummary
            paper={paper}
            brief={aiBrief}
          />
        )}

        {canTriage && (
          <div className="evidence-drawer__details-triage-action" style={{ marginBottom: '24px' }}>
            <button 
              className={`evidence-drawer__action-button ${isTriaging ? 'evidence-drawer__batch-button--loading' : ''}`}
              onClick={handleTriage}
              disabled={isTriaging}
              style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '10px' }}
            >
              {isTriaging ? (
                <>
                  <div className="evidence-drawer__batch-spinner" />
                  Analyzing abstract...
                </>
              ) : (
                'Analyze with AI'
              )}
            </button>
            {triageError && (
              <div className="evidence-drawer__error" style={{ marginTop: '8px' }}>
                {triageError}
              </div>
            )}
          </div>
        )}
        
        {paper.abstract && (
          <div className="evidence-drawer__abstract-section">
            <h4 className="evidence-drawer__abstract-label">Abstract</h4>
            <div 
              className="evidence-drawer__details-abstract"
              style={{ 
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'visible'
              }}
            >
              {processAbstractText(paper.abstract)}
            </div>
          </div>
        )}
        
        {validAuthors.length > 0 && (
          <div className="evidence-drawer__paper-meta" style={{ marginBottom: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <strong style={{ display: 'block', marginBottom: '0', fontSize: '13px', opacity: 0.9 }}>Authors:</strong>
            <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
              {validAuthors.join(', ')}
            </div>
          </div>
        )}
        
        <div className="evidence-drawer__details-links">
          {paper.doi && (
            <a 
              href={`https://doi.org/${paper.doi}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="evidence-drawer__link-item"
            >
              <span className="evidence-drawer__link-label">DOI:</span> {paper.doi}
            </a>
          )}
          {paper.url && (
            <a 
              href={paper.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="evidence-drawer__link-item evidence-drawer__link-item--primary"
            >
              View on PubMed →
            </a>
          )}
        </div>
        <div className="evidence-drawer__details-actions">
          <button 
            className="evidence-drawer__action-button" 
            onClick={onSaveToLibrary}
            disabled={!onSaveToLibrary}
          >
            Save to Library
          </button>
          <button 
            className="evidence-drawer__action-button" 
            onClick={onAttachToScenario}
            disabled={!onAttachToScenario}
          >
            Attach to Scenario
          </button>
        </div>
      </div>
    </div>
  );
};
